import React, { useState } from 'react';
import { FaRobot, FaSpinner } from 'react-icons/fa';

const RISK_STYLES = {
  low: 'bg-green-100 text-green-700',
  moderate: 'bg-amber-100 text-amber-700',
  high: 'bg-red-100 text-red-700',
  unknown: 'bg-gray-100 text-gray-600',
};

// AI health analysis panel: generate + display the latest poultry health report.
export default function HealthReportPanel({ report, loading, onGenerate, range, onRangeChange }) {
  const [open, setOpen] = useState(true);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
      <div className="flex items-center justify-between p-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
            <FaRobot />
          </span>
          <div>
            <h3 className="text-sm font-semibold text-gray-800">AI Health Analysis</h3>
            <p className="text-xs text-gray-500">Insights from recent climate trends</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={range}
            onChange={(e) => onRangeChange(e.target.value)}
            className="text-xs border border-gray-200 rounded px-2 py-1 text-gray-600"
          >
            <option value="24h">Last 24h</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
          </select>
          <button
            onClick={onGenerate}
            disabled={loading}
            className="text-xs px-3 py-1.5 rounded-lg bg-primary text-white font-medium hover:bg-primary/90 disabled:opacity-60 flex items-center gap-1.5"
          >
            {loading ? <FaSpinner className="animate-spin" /> : <FaRobot />}
            {loading ? 'Analyzing…' : 'Generate'}
          </button>
        </div>
      </div>

      {!report ? (
        <div className="p-6 text-center text-sm text-gray-400">
          Generate an AI analysis of this house's recent climate to surface possible health risks.
        </div>
      ) : (
        <div className="p-4 space-y-4">
          <button
            onClick={() => setOpen((o) => !o)}
            className="w-full flex items-center justify-between text-left"
          >
            <div className="flex items-center gap-2">
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase ${RISK_STYLES[report.riskLevel] || RISK_STYLES.unknown}`}>
                {report.riskLevel} risk
              </span>
              <span className="text-xs text-gray-400">
                {new Date(report.createdAt).toLocaleString('en-GB')} · {report.range}
              </span>
            </div>
            <span className="text-xs text-primary">{open ? 'Hide' : 'Show'}</span>
          </button>

          {open && (
            <>
              {report.summary && <p className="text-sm text-gray-700">{report.summary}</p>}

              <Section title="Possible risks" items={report.risks} />
              <Section title="Possible causes" items={report.possibleCauses} />
              <Section title="Recommended actions" items={report.recommendedActions} />

              <div className="text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-lg p-3 italic">
                ⚠️ {report.disclaimer}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ title, items }) {
  if (!items?.length) return null;
  return (
    <div>
      <h4 className="text-xs font-semibold uppercase text-gray-500 mb-1">{title}</h4>
      <ul className="list-disc list-inside space-y-1">
        {items.map((it, i) => (
          <li key={i} className="text-sm text-gray-700">{it}</li>
        ))}
      </ul>
    </div>
  );
}
